/**
 * ReviewNavigation Component
 *
 * Navigation component for the multi-page evidence review interface.
 * Displays page tabs/steps with completion indicators and handles
 * navigation between the four review pages.
 *
 * Pages:
 * 1. Ground Risk Answers
 * 2. Ground Risk Mitigations
 * 3. Air Risk Answers
 * 4. Air Risk Mitigations
 *
 * Requirements: 10.1, 10.3
 */

import React, { useState, useCallback } from 'react';
import {
  CheckmarkCircleRegular,
  CircleRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  CheckmarkRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import styles from './ReviewNavigation.module.css';

export interface ReviewNavigationProps {
  /** Current active page number (1-4) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Labels for each page */
  pageLabels: string[];
  /** Array of page numbers that have been completed */
  completedPages: number[];
  /** Callback when user navigates to a different page */
  onNavigate: (page: number) => void;
  /** Callback when user clicks "Complete Review" */
  onComplete: () => void;
  /** Whether there are unsaved changes that should trigger a warning */
  hasUnsavedChanges: boolean;
}

/**
 * ReviewNavigation provides navigation between the four review pages
 * with visual indicators for completion status and progress tracking.
 *
 * Features:
 * - Page tabs with completion indicators (checkmarks)
 * - Current page highlighting
 * - Progress indicator (e.g., "Page 2 of 4")
 * - Previous/Next navigation buttons
 * - "Complete Review" button (enabled when all pages completed)
 * - Unsaved changes warning dialog
 *
 * @example
 * <ReviewNavigation
 *   currentPage={2}
 *   totalPages={4}
 *   pageLabels={['Ground Risk Answers', 'Ground Risk Mitigations', 'Air Risk Answers', 'Air Risk Mitigations']}
 *   completedPages={[1]}
 *   onNavigate={(page) => setCurrentPage(page)}
 *   onComplete={() => handleComplete()}
 *   hasUnsavedChanges={false}
 * />
 */
export const ReviewNavigation: React.FC<ReviewNavigationProps> = ({
  currentPage,
  totalPages,
  pageLabels,
  completedPages,
  onNavigate,
  onComplete,
  hasUnsavedChanges,
}) => {
  // State for unsaved changes warning dialog
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<number | null>(null);

  // Check if all pages are completed
  const allPagesCompleted = completedPages.length === totalPages;

  // Calculate progress percentage
  const progressPercentage = (completedPages.length / totalPages) * 100;

  /**
   * Handle navigation with unsaved changes check
   */
  const handleNavigate = useCallback(
    (targetPage: number) => {
      if (targetPage < 1 || targetPage > totalPages || targetPage === currentPage) {
        return;
      }

      if (hasUnsavedChanges) {
        setPendingNavigation(targetPage);
        setShowWarningDialog(true);
      } else {
        onNavigate(targetPage);
      }
    },
    [currentPage, totalPages, hasUnsavedChanges, onNavigate]
  );

  /**
   * Handle previous page navigation
   */
  const handlePrevious = useCallback(() => {
    if (currentPage > 1) {
      handleNavigate(currentPage - 1);
    }
  }, [currentPage, handleNavigate]);

  /**
   * Handle next page navigation
   */
  const handleNext = useCallback(() => {
    if (currentPage < totalPages) {
      handleNavigate(currentPage + 1);
    }
  }, [currentPage, totalPages, handleNavigate]);

  /**
   * Confirm navigation despite unsaved changes
   */
  const confirmNavigation = useCallback(() => {
    if (pendingNavigation !== null) {
      onNavigate(pendingNavigation);
    }
    setShowWarningDialog(false);
    setPendingNavigation(null);
  }, [pendingNavigation, onNavigate]);

  /**
   * Cancel navigation and stay on current page
   */
  const cancelNavigation = useCallback(() => {
    setShowWarningDialog(false);
    setPendingNavigation(null);
  }, []);

  /**
   * Handle complete review action
   */
  const handleComplete = useCallback(() => {
    if (allPagesCompleted) {
      onComplete();
    }
  }, [allPagesCompleted, onComplete]);

  /**
   * Check if a page is completed
   */
  const isPageCompleted = (pageNum: number): boolean => {
    return completedPages.includes(pageNum);
  };

  return (
    <nav className={styles.container} aria-label="Review navigation">
      {/* Progress Bar */}
      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${progressPercentage}%` }}
            role="progressbar"
            aria-valuenow={completedPages.length}
            aria-valuemin={0}
            aria-valuemax={totalPages}
            aria-label={`${completedPages.length} of ${totalPages} pages completed`}
          />
        </div>
        <span className={styles.progressText}>
          {completedPages.length} of {totalPages} pages completed
        </span>
      </div>

      {/* Page Tabs */}
      <div className={styles.tabsContainer}>
        <div className={styles.tabs} role="tablist" aria-label="Review pages">
          {pageLabels.map((label, index) => {
            const pageNum = index + 1;
            const isActive = pageNum === currentPage;
            const isCompleted = isPageCompleted(pageNum);

            return (
              <button
                key={pageNum}
                className={`${styles.tab} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}
                onClick={() => handleNavigate(pageNum)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`review-page-${pageNum}`}
                tabIndex={isActive ? 0 : -1}
              >
                <span className={styles.tabIndicator}>
                  {isCompleted ? (
                    <CheckmarkCircleRegular className={styles.tabIconCompleted} aria-hidden="true" />
                  ) : (
                    <CircleRegular className={styles.tabIcon} aria-hidden="true" />
                  )}
                </span>
                <span className={styles.tabNumber}>{pageNum}</span>
                <span className={styles.tabLabel}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation Controls */}
      <div className={styles.controls}>
        <button
          className={styles.navButton}
          onClick={handlePrevious}
          disabled={currentPage === 1}
          aria-label="Go to previous page"
        >
          <ChevronLeftRegular className={styles.navIcon} aria-hidden="true" />
          <span>Previous</span>
        </button>

        <span className={styles.pageIndicator} aria-live="polite">
          Page {currentPage} of {totalPages}
        </span>

        {currentPage < totalPages ? (
          <button
            className={styles.navButton}
            onClick={handleNext}
            aria-label="Go to next page"
          >
            <span>Next</span>
            <ChevronRightRegular className={styles.navIcon} aria-hidden="true" />
          </button>
        ) : (
          <button
            className={`${styles.navButton} ${styles.completeButton}`}
            onClick={handleComplete}
            disabled={!allPagesCompleted}
            aria-label={
              allPagesCompleted
                ? 'Complete review'
                : `Cannot complete review - ${totalPages - completedPages.length} pages remaining`
            }
          >
            <CheckmarkRegular className={styles.navIcon} aria-hidden="true" />
            <span>Complete Review</span>
          </button>
        )}
      </div>

      {/* Unsaved Changes Warning Dialog */}
      {showWarningDialog && (
        <div className={styles.dialogOverlay} role="dialog" aria-modal="true" aria-labelledby="warning-title">
          <div className={styles.dialog}>
            <div className={styles.dialogHeader}>
              <WarningRegular className={styles.dialogIcon} aria-hidden="true" />
              <h2 id="warning-title" className={styles.dialogTitle}>
                Unsaved Changes
              </h2>
            </div>
            <p className={styles.dialogMessage}>
              You have unsaved changes on this page. Are you sure you want to navigate away?
              Your changes will be lost.
            </p>
            <div className={styles.dialogActions}>
              <button
                className={styles.dialogButtonSecondary}
                onClick={cancelNavigation}
              >
                Stay on Page
              </button>
              <button
                className={styles.dialogButtonPrimary}
                onClick={confirmNavigation}
              >
                Leave Page
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default ReviewNavigation;
